import { ILogger } from '@remento/logger';
import { PersistentStorage } from '@remento/web-utils/storage';
import { v4 as uuid } from 'uuid';

import { Payload, Router } from '../../router.js';

import { PageExitActivityEvent, PageExitActivityPayload, PageInitActivityEvent } from './@types.js';
import { PersistentActivityRepository } from './activity-repository.js';

const ACTIVITY_PLUGIN_ID = 'activity-plugin';

export class ActivityObserver {
  private repository: PersistentActivityRepository;
  public sessionId: string;
  public sessionStartOn: number;
  public pageDuration: number;
  public pageActiveStartTime: number;
  private logger: ILogger;

  constructor(
    private router: Router,
    source: string,
    sessionTTL: number,
    private pageTTL: number,
  ) {
    this.logger = router.logger.extend('activity-observer');
    this.repository = new PersistentActivityRepository(new PersistentStorage(source), sessionTTL);
    this.sessionId = this.repository.getSessionId();
    this.sessionStartOn = this.repository.getSessionStartOn();
    this.pageDuration = 0;
    this.pageActiveStartTime = Date.now();
  }

  run() {
    const router = this.router;
    const repository = this.repository;

    let active = true;
    let exited = false;
    let exitTimeout: number | null = null;
    let path: string = window.location.pathname;

    const synchronizeSessionDuration = () => {
      repository.saveSession(this.sessionId, this.sessionStartOn);
    };

    const checkPageChange = () => {
      const currentPath = window.location.pathname;
      if (currentPath == path) {
        return;
      }
      path = currentPath;
      this.logger.debug('PAGE_CHANGE');
      // Trigger the exit event
      const payload = {
        data: { cause: 'pagechange' },
        properties: {},
      } as Payload<PageExitActivityPayload>;
      router.publish(PageExitActivityEvent, payload);
      // Trigger the arrive event
      router.publish(PageInitActivityEvent, { data: null, properties: {} });
    };

    setInterval(() => {
      checkPageChange();
    }, 500);

    window.addEventListener('beforeunload', () => {
      if (exited) return;
      const payload = {
        data: { cause: 'unload' },
        properties: {},
      } as Payload<PageExitActivityPayload>;
      this.logger.debug('PAGE_UNLOAD');
      router.publish(PageExitActivityEvent, payload);
      synchronizeSessionDuration();
    });
    window.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') {
        if (exitTimeout != null) clearTimeout(exitTimeout);
        active = true;
        this.pageActiveStartTime = Date.now();
        if (repository.isSessionExpired()) {
          // Reset all duration
          this.pageDuration = 0;
          this.sessionId = uuid();
          this.sessionStartOn = Date.now();
        } else {
          // Recover the session duration from local storage (which may have been updated by a different tab)
          this.sessionId = repository.getSessionId();
          this.sessionStartOn = repository.getSessionStartOn();
        }
        // Grab the latest session ID or generate a new one if necessary

        if (exited) {
          // Reset the page duration
          this.pageDuration = 0;
          // The exit timer fired due to inactivity, act as if they were loading the page again
          const payload = { data: null, properties: {} };
          this.logger.debug('PAGE_ACTIVE');
          router.publish(PageInitActivityEvent, payload);
          exited = false;
        }

        synchronizeSessionDuration();
      } else {
        synchronizeSessionDuration();
        active = false;
        this.pageDuration += Date.now() - this.pageActiveStartTime;
        this.pageActiveStartTime = Date.now();

        exitTimeout = setTimeout(() => {
          const payload = {
            data: { cause: 'inactive' },
            properties: {},
          } as Payload<PageExitActivityPayload>;
          // Send the same event as if the user had unloaded the page
          this.logger.debug('PAGE_INACTIVE_TIMEOUT');
          router.publish(PageExitActivityEvent, payload);
          exited = true;
        }, this.pageTTL);
      }
    });
    setTimeout(() => router.publish(PageInitActivityEvent, { data: null, properties: {} }), 0);
  }
}

export function registerActivityPlugin(
  router: Router,
  source: string,
  sessionTTL: number,
  pageTTL: number,
): ActivityObserver {
  if (router.hasPlugin(ACTIVITY_PLUGIN_ID)) {
    return router.getPlugin(ACTIVITY_PLUGIN_ID)!;
  }
  const activityPlugin = new ActivityObserver(router, source, sessionTTL, pageTTL);
  router.registerPlugin(ACTIVITY_PLUGIN_ID, activityPlugin);
  activityPlugin.run();
  return activityPlugin;
}
